import { ReactNode } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ArrowRightStartOnRectangleIcon, BellIcon, ChevronLeftIcon, Cog6ToothIcon, MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import { SearchField } from "../primitives/SearchField";
import { Skeleton } from "../primitives/Skeleton";
import { focusRing } from "@ui/tokens/interaction";
import { glassPopover } from "@ui/tokens/glass";

interface TopBarProps {
  userName: string;
  userInitials: string;
  hasNotifications?: boolean;
  /** The one door in. The bar holds its place; the menu itself belongs to the shell. */
  addSlot: ReactNode;
  onBack?: () => void;
  onSearch?: (value: string) => void;
  onNotifications?: () => void;
  onLogout?: () => void;
  onSettings?: () => void;
  isDark?: boolean;
  onToggleTheme?: () => void;
}

const roundControl =
  "relative inline-flex items-center justify-center shrink-0 h-control rounded-pill bg-tint/[0.06] transition duration-hover hover:bg-tint/10";

const menuEntry =
  "flex items-center gap-2.5 w-full h-11 px-3 rounded-xl text-left text-control text-ink transition duration-hover data-[focus]:bg-tint/10";

export const TopBar = ({
  userName,
  userInitials,
  hasNotifications = false,
  addSlot,
  onBack,
  onSearch,
  onNotifications,
  onLogout,
  onSettings,
  isDark = true,
  onToggleTheme,
}: TopBarProps) => (
  <header className="flex items-center shrink-0 gap-5 h-topbar px-9 bg-surface-bar border-b border-transparent">
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        aria-label="Retour"
        className={`inline-flex items-center justify-center shrink-0 w-control h-control rounded-pill border border-[color:var(--back-border)] bg-[var(--back-surface)] text-[color:var(--back-ink)] transition duration-hover hover:bg-[var(--back-hover-surface)] hover:border-[color:var(--back-hover-border)] ${focusRing}`}
      >
        <ChevronLeftIcon className="w-6 h-6" strokeWidth={1.8} aria-hidden="true" />
      </button>
    )}

    <SearchField
      placeholder="Rechercher une ressource ou une classe…"
      onChange={(event) => onSearch?.(event.target.value)}
      className="w-search shrink min-w-0"
    />

    {/* The void is deliberate: it gives the actions room and keeps the bar's three-beat
        rhythm — search, void, actions. A search stretched to 1200px is a website reflex. */}
    <span className="flex-1" />

    {/* The only ink element of the bar, and the only one carrying the pink rule. */}
    {addSlot}

    <button
      type="button"
      onClick={onNotifications}
      aria-label="Notifications"
      className={`${roundControl} w-control text-ink-bell ${focusRing}`}
    >
      <BellIcon className="w-6 h-6" strokeWidth={1.8} aria-hidden="true" />
      {hasNotifications && (
        <span
          aria-hidden="true"
          className="absolute top-3 right-3.25 w-2.25 h-2.25 rounded-full bg-signature ring-2 ring-surface-content"
        />
      )}
    </button>

    <Menu>
      <MenuButton className={`${roundControl} gap-3 pl-1.5 pr-5 ${focusRing}`}>
        {userInitials ? (
          <span className="flex items-center justify-center shrink-0 w-9 h-9 rounded-pill bg-surface-avatar text-body font-700 text-on-action">
            {userInitials}
          </span>
        ) : (
          <Skeleton className="shrink-0 w-9 h-9 rounded-pill" />
        )}
        {userName ? (
          <span className="text-control-lg font-500 text-ink whitespace-nowrap">{userName}</span>
        ) : (
          <Skeleton className="h-4 w-[120px] rounded-sm" />
        )}
      </MenuButton>
      {(onLogout || onToggleTheme || onSettings) && (
        <MenuItems
          anchor={{ to: "bottom end", gap: 8 }}
          className={`z-50 w-64 p-1.5 rounded-2xl outline-none ${glassPopover}`}
        >
          {onSettings && (
            <MenuItem>
              <button type="button" onClick={onSettings} className={menuEntry}>
                <Cog6ToothIcon className="w-5 h-5 text-ink-help" aria-hidden="true" />
                Paramètres
              </button>
            </MenuItem>
          )}
          {onToggleTheme && (
            <MenuItem>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  onToggleTheme();
                }}
                className={`${menuEntry} justify-between`}
              >
                <span className="flex items-center gap-2.5">
                  {isDark ? (
                    <MoonIcon className="w-5 h-5 text-ink-help" aria-hidden="true" />
                  ) : (
                    <SunIcon className="w-5 h-5 text-ink-help" aria-hidden="true" />
                  )}
                  Thème {isDark ? "sombre" : "clair"}
                </span>
                <span
                  aria-hidden="true"
                  className="relative inline-flex items-center w-11 h-6 rounded-pill bg-tint/15 transition duration-hover"
                >
                  <span
                    className={`absolute left-0.5 w-5 h-5 rounded-full bg-ink flex items-center justify-center transition-transform duration-hover ${
                      isDark ? "translate-x-0" : "translate-x-5"
                    }`}
                  >
                    {isDark ? (
                      <MoonIcon className="w-3 h-3 text-surface-content" strokeWidth={2.4} />
                    ) : (
                      <SunIcon className="w-3 h-3 text-surface-content" strokeWidth={2.4} />
                    )}
                  </span>
                </span>
              </button>
            </MenuItem>
          )}
          {onLogout && (
            <>
              <div aria-hidden="true" className="my-1.5 mx-2 h-px bg-tint/10" />
              <MenuItem>
                <button
                  type="button"
                  onClick={onLogout}
                  className={`${menuEntry} data-[focus]:!bg-danger/15 data-[focus]:text-danger`}
                >
                  <ArrowRightStartOnRectangleIcon className="w-5 h-5" aria-hidden="true" />
                  Se déconnecter
                </button>
              </MenuItem>
            </>
          )}
        </MenuItems>
      )}
    </Menu>
  </header>
);
